import { CircularProgress, IconButton } from '@material-ui/core';
import { Lock, LockOpen, PartlyLocked } from 'Components/Atoms/Icons';
import Box from 'Components/Atoms/Box';
import Select from 'Components/Atoms/Select';
import Typography from 'Components/Atoms/Typography';
import { ReservationContext } from 'Contexts/ReservationContext';
import React, { useContext, useEffect, useMemo } from 'react';
import { Space } from 'types/reservations';
import server from 'utils/server';
import { RestaurantContext, Severity } from 'Contexts/RestaurantContext';
import { ReservationShift } from 'types/shared/helper/shifts';
import usePinAuth from 'CustomHooks/usePinAuth';
import { SecurityGrant, trackChange } from 'utils/securityActivity/grant';
import useToast from 'CustomHooks/useToast';
import { useSwipeable } from 'react-swipeable';
import {
  useDrop as useReactDrop,
  useDragLayer as useReactDragLayer,
} from 'react-dnd';
import SpaceBlockModal from './SpaceBlockModal';
import { shiftBlockableForTable } from 'utils/tableBlocks';

type Props = {
  value: string | null;
  onChange: (nV: string) => void;
  spaces: Space[];
  title?: string;
  menuClassName?: string;
  actionsDisabled?: boolean;
};

const DRAG_SPACE_SWITCH_DELAY_MS = 180;

const SpaceDropButton: React.FC<{
  space: Space;
  active: boolean;
  onSwitch: (id: string) => void;
  registerButton: (id: string, node: HTMLButtonElement | null) => void;
}> = ({ space, active, onSwitch, registerButton }) => {
  const [{ isOver }, drop] = useReactDrop({
    accept: 'reservation',
    drop: () => undefined,
    collect: (m) => ({ isOver: m.isOver() }),
  });

  const buttonRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      registerButton(space.id, node);
      drop(node);
    },
    [drop, registerButton, space.id]
  );

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={() => onSwitch(space.id)}
      className={`space-drop-btn${active ? ' active' : ''}${
        isOver ? ' over' : ''
      }`}
    >
      {space.name}
    </button>
  );
};

const SpaceSwitch: React.FC<Props> = ({
  value,
  onChange,
  spaces,
  title = 'Space',
  menuClassName = 'spaceSwitchMenu',
  actionsDisabled = false,
}) => {
  const options = spaces.map((s) => ({ id: s.id, label: s.name }));

  const { restaurantId, experimentalFlags } = useContext(RestaurantContext);

  const { shifts, floorPlanProps, date } = useContext(ReservationContext);

  const dragLayer = useReactDragLayer((monitor) => ({
    currentOffset: monitor.getClientOffset(),
    dragging: monitor.isDragging() && monitor.getItemType() === 'reservation',
  }));
  const isDraggingReservation = dragLayer.dragging;
  const spaceButtonRefs = React.useRef<Record<string, HTMLButtonElement | null>>(
    {}
  );
  const lastDragSwitchRef = React.useRef<string | null>(null);
  const currentSpaceRef = React.useRef<string | null>(value);
  const pendingDragSwitchRef = React.useRef<{
    id: string;
    timeout: ReturnType<typeof window.setTimeout>;
  } | null>(null);

  const showButtons =
    !!experimentalFlags?.data?.switchSpaceOnDrag &&
    isDraggingReservation &&
    spaces.length > 1 &&
    !actionsDisabled;

  useEffect(() => {
    currentSpaceRef.current = value;
  }, [value]);

  const registerButton = React.useCallback(
    (id: string, node: HTMLButtonElement | null) => {
      spaceButtonRefs.current[id] = node;
    },
    []
  );

  const clearPendingDragSwitch = React.useCallback(() => {
    if (!pendingDragSwitchRef.current) return;

    window.clearTimeout(pendingDragSwitchRef.current.timeout);
    pendingDragSwitchRef.current = null;
  }, []);

  const scheduleDragSpaceSwitch = React.useCallback(
    (spaceId: string) => {
      if (spaceId === currentSpaceRef.current) {
        clearPendingDragSwitch();
        return;
      }

      if (lastDragSwitchRef.current === spaceId) return;
      if (pendingDragSwitchRef.current?.id === spaceId) return;

      clearPendingDragSwitch();
      pendingDragSwitchRef.current = {
        id: spaceId,
        timeout: window.setTimeout(() => {
          pendingDragSwitchRef.current = null;

          if (spaceId === currentSpaceRef.current) return;

          lastDragSwitchRef.current = spaceId;
          onChange(spaceId);
        }, DRAG_SPACE_SWITCH_DELAY_MS),
      };
    },
    [clearPendingDragSwitch, onChange]
  );

  const switchSpaceFromDragPosition = React.useCallback(() => {
    const offset = dragLayer.currentOffset;
    if (!showButtons || !offset) {
      clearPendingDragSwitch();
      return;
    }

    const nextSpace = spaces.find((space) => {
      const node = spaceButtonRefs.current[space.id];
      if (!node) return false;

      const rect = node.getBoundingClientRect();
      return (
        offset.x >= rect.left &&
        offset.x <= rect.right &&
        offset.y >= rect.top &&
        offset.y <= rect.bottom
      );
    });

    if (!nextSpace || nextSpace.id === currentSpaceRef.current) {
      clearPendingDragSwitch();
      return;
    }

    scheduleDragSpaceSwitch(nextSpace.id);
  }, [
    clearPendingDragSwitch,
    dragLayer.currentOffset,
    scheduleDragSpaceSwitch,
    showButtons,
    spaces,
  ]);

  useEffect(() => {
    if (!isDraggingReservation) {
      clearPendingDragSwitch();
      lastDragSwitchRef.current = null;
      return;
    }

    switchSpaceFromDragPosition();
  }, [
    clearPendingDragSwitch,
    isDraggingReservation,
    switchSpaceFromDragPosition,
  ]);

  useEffect(() => clearPendingDragSwitch, [clearPendingDragSwitch]);

  const closed = useMemo(() => {
    let openShifts = shifts?.filter((s) => s.active && !s.commentOnly) ?? [];

    let whereSpaceIsClosed = openShifts.filter(
      (s) =>
        !value ||
        s.closed ||
        (s.spaces.length && !s.spaces.includes(value) && s.matchBoth) ||
        (s.overwrite as any)?.closedSpaces?.includes(value) ||
        !!s.overwrite?.blockReservations
    );

    if (
      whereSpaceIsClosed.length &&
      whereSpaceIsClosed.length === openShifts.length
    )
      return 2;
    if (!whereSpaceIsClosed.length) return 0;
    return 1;
  }, [shifts, value]);

  const [loading, setLoading] = React.useState(false);
  const [blockModalOpen, setBlockModalOpen] = React.useState(false);

  const reachableShifts = useMemo(() => {
    const spaceTables =
      floorPlanProps.floorPlan?.tables?.filter((t) => t.space === value) ?? [];

    return (shifts ?? []).filter(
      (s) =>
        s.active &&
        !s.commentOnly &&
        spaceTables.some((t) => shiftBlockableForTable(s as any, t))
    );
  }, [shifts, floorPlanProps.floorPlan, value]);

  const blockPerShift = !!experimentalFlags?.data?.blockPerShift;

  const { authenticate } = usePinAuth();
  const toast = useToast();

  // The restaurant and day on screen, read after the request: a response for
  // one the user already left must not replace its shifts.
  const screenKeyRef = React.useRef('');
  screenKeyRef.current = `${restaurantId}|${date}`;

  // The PIN opened the per-shift dialog; its changes are logged under that grant.
  const modalGrant = React.useRef<SecurityGrant | undefined>(undefined);

  const closeSpace = async (
    isClosed: boolean,
    shiftIds?: string[],
    grant = modalGrant.current
  ) => {
    try {
      if (loading || !restaurantId || !date) return;
      setLoading(true);
      const requestKey = `${restaurantId}|${date}`;
      const { data } = await trackChange(
        grant,
        isClosed ? 'space.block' : 'space.unblock',
        server.post<{ shifts: ReservationShift[] }>(
          `/v03/shifts/${restaurantId}/${date}/closeSpace`,
          {
            spaceId: value,
            closed: isClosed,
            ...(shiftIds ? { shiftIds } : {}),
          }
        ),
        {
          targets: [
            { type: 'space', id: value ?? '' },
            ...(shiftIds ?? []).map((id) => ({ type: 'shift' as const, id })),
          ],
          changes: [
            { field: 'blocked', before: !isClosed, after: isClosed },
            { field: 'date', before: null, after: date },
          ],
        }
      );

      if (screenKeyRef.current === requestKey)
        floorPlanProps.setShifts(data.shifts);
    } catch (error) {
      toast('Bereich konnte nicht geändert werden.', Severity.ERROR);
    }
    setLoading(false);
  };

  const toggleSpace = async (grant?: SecurityGrant) => {
    let openShifts = shifts?.filter((s) => s.active && !s.commentOnly) ?? [];

    let whereSpaceIsClosed = openShifts.filter(
      (s) =>
        !value ||
        s.closed ||
        (s.spaces.length && !s.spaces.includes(value) && s.matchBoth) ||
        !!s.overwrite?.blockReservations
    );

    if (
      whereSpaceIsClosed.length === openShifts.length ||
      openShifts.length === 0
    ) {
      return toast(
        'Bereich ist durch andere Regeln geschlossen.',
        Severity.ERROR
      );
    }

    closeSpace(closed !== 2, undefined, grant);
  };

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      let index = spaces.findIndex((s) => s.id === value);

      if (index === -1 || eventData.dir === 'Up' || eventData.dir === 'Down')
        return;

      if (eventData.dir === 'Right') {
        if (index > 0) {
          index--;
        } else {
          index = spaces.length - 1;
        }
      } else if (eventData.dir === 'Left') {
        index = (index + 1) % spaces.length;
      }

      onChange(spaces[index]?.id ?? value);
    },
  });

  return (
    <Box
      className={`spaceSwitch${showButtons ? ' spaceSwitch--buttons' : ''}`}
      width={showButtons ? undefined : 200}
      {...handlers}
    >
      <Typography
        translation="reservations"
        variant="text-4"
        className="mg-bt-xs"
        style={{ maxWidth: 170 }}
      >
        {title}
      </Typography>
      {showButtons ? (
        <Box className="space-drop-btn-row">
          {spaces.map((s) => (
            <SpaceDropButton
              key={s.id}
              space={s}
              active={s.id === value}
              onSwitch={onChange}
              registerButton={registerButton}
            />
          ))}
        </Box>
      ) : (
        <Box flex width={170}>
          <Select
            value={value}
            options={options}
            menuClassName={menuClassName}
            onChange={(e: any) => onChange(e.target.value)}
          />
          <IconButton
            disabled={actionsDisabled}
            title={
              blockPerShift
                ? 'Bereich sperren'
                : closed === 2
                ? 'Bereich für den ganzen Tag freigeben'
                : 'Bereich für den ganzen Tag sperren'
            }
            onClick={() =>
              authenticate(
                'reservations.spaceBlocked',
                (_, grant) => {
                  if (blockPerShift) {
                    modalGrant.current = grant;
                    setBlockModalOpen(true);
                  } else {
                    toggleSpace(grant);
                  }
                },
                undefined,
                {
                  action: blockPerShift
                    ? 'space.blockDialog'
                    : closed !== 2
                    ? 'space.block'
                    : 'space.unblock',
                  category: blockPerShift ? 'form' : undefined,
                  targets: [{ type: 'space', id: value ?? '' }],
                }
              )
            }
          >
            {loading && <CircularProgress size={24} />}
            {!loading && closed === 2 && <Lock color="error" />}
            {!loading && closed === 1 && <PartlyLocked color="primary" />}
            {!loading && closed === 0 && <LockOpen color="primary" />}
          </IconButton>
        </Box>
      )}
      {blockPerShift && !!value && (
        <SpaceBlockModal
          open={blockModalOpen}
          onClose={() => setBlockModalOpen(false)}
          spaceId={value}
          shifts={reachableShifts}
          loading={loading}
          onToggle={closeSpace}
        />
      )}
    </Box>
  );
};

export default SpaceSwitch;
